import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { SpaceBetween, Button, Box } from '@cloudscape-design/components';
import { useHistory } from 'react-router-dom';
import FundingRequestTable from '../components/FundingRequestTable';
import ProgramScopeSelector from '../components/ProgramScopeSelector';
import { s3Service } from '../services/s3Service';
import { getFundRequestDetailsUrl } from '../utils/urlHelper';
import { useAuth } from '../hooks/useAuth';
import { getProgramCodes } from '../programs/registry';
import { buildFundingKey, peekFundingSnapshot, clearFundingSnapshot, saveFundingSnapshot } from '../services/fundingRequestsCache';

const DEFAULT_SHOW_ALL_DAYS = 30;

const FundingRequestTracker = () => {
  // Read the snapshot once for restore. It is cleared after mount in an effect
  // below (not in this initializer, which StrictMode double-invokes), so a
  // genuinely-empty revalidation that skips the save cannot leave stale data.
  const [restoredSnapshot] = useState(() => peekFundingSnapshot());
  const [fundingRequests, setFundingRequests] = useState(() => restoredSnapshot?.state?.fundingRequests ?? []);
  const [loading, setLoading] = useState(false);
  const [showAll, setShowAll] = useState(() => !!restoredSnapshot?.state?.showAll);
  const [daysFilter, setDaysFilter] = useState(() => restoredSnapshot?.state?.daysFilter ?? null);
  const [programScope, setProgramScope] = useState(getProgramCodes());
  const history = useHistory();
  const { user, loading: authLoading } = useAuth();

  const userAlias = authLoading ? undefined : (user?.email ? user.email.split('@')[0] : null);

  const handleViewRequest = (requestId) => {
    history.push(getFundRequestDetailsUrl(requestId));
  };

  const loadFundingRequests = useCallback(async (isShowAll, days, { background = false } = {}) => {
    if (userAlias === null) {
      setFundingRequests([]);
      return;
    }
    if (!background) setLoading(true);
    try {
      const data = isShowAll
        ? await s3Service.getFundingRequests(null, days)
        : await s3Service.getFundingRequests(userAlias);
      setFundingRequests(data);
    } catch (error) {
      console.error('Error loading funding requests:', error);
      if (!background) setFundingRequests([]);
    } finally {
      if (!background) setLoading(false);
    }
  }, [userAlias]);

  // ── Stale-while-revalidate across navigation (see fundingRequestsCache) ──────
  // View key recorded on the snapshot (My per alias / Show All per day window).
  const viewKey = useMemo(
    () => buildFundingKey(showAll, daysFilter, userAlias),
    [showAll, daysFilter, userAlias]
  );

  // Table view (filter/sort/page) restored from the snapshot at first render and
  // reported back up so it can be saved again.
  const initialTableView = restoredSnapshot?.state?.tableView ?? null;
  const hadSnapshot = !!restoredSnapshot;
  const tableViewRef = useRef(initialTableView);
  const handleTableViewChange = useCallback((v) => { tableViewRef.current = v; }, []);

  // Snapshot-able state, kept current every render for the unmount handler.
  const snapStateRef = useRef({});
  snapStateRef.current = { key: viewKey, fundingRequests, showAll, daysFilter };

  // Clear the consumed snapshot after mount (kept out of the initializer so it
  // is StrictMode-safe). A skipped save on an empty revalidation then leaves no
  // stale snapshot for the next visit.
  useEffect(() => { clearFundingSnapshot(); }, []);

  // The view (list/showAll/days/table view) is seeded from the snapshot in the
  // useState initializers, so first render already shows it. Here we only
  // revalidate: a full background reload when a snapshot was restored, else a
  // normal load.
  useEffect(() => {
    if (userAlias === undefined) return;
    loadFundingRequests(showAll, daysFilter, { background: hadSnapshot });
  }, [userAlias]); // eslint-disable-line react-hooks/exhaustive-deps

  // Save the snapshot on unmount (route change). Skip empty snapshots (nothing
  // loaded yet, e.g. StrictMode's dev mount/unmount before the first load).
  useEffect(() => {
    return () => {
      const s = snapStateRef.current;
      if (!s.fundingRequests || s.fundingRequests.length === 0) return;
      saveFundingSnapshot(s.key, {
        fundingRequests: s.fundingRequests,
        showAll: s.showAll,
        daysFilter: s.daysFilter,
        tableView: tableViewRef.current,
      });
    };
  }, []);

  const handleToggleShowAll = () => {
    const nextShowAll = !showAll;
    const nextDays = nextShowAll ? DEFAULT_SHOW_ALL_DAYS : null;
    setShowAll(nextShowAll);
    setDaysFilter(nextDays);
    loadFundingRequests(nextShowAll, nextDays);
  };

  const handleDaysFilterChange = (newDays) => {
    setDaysFilter(newDays);
    loadFundingRequests(showAll, newDays);
  };

  const filteredByProgram = programScope.length === getProgramCodes().length
    ? fundingRequests
    : fundingRequests.filter(fr => !fr.program || programScope.includes(fr.program));

  return (
    <SpaceBetween size="l">
      <Box float="right">
        <SpaceBetween direction="horizontal" size="xs">
          <ProgramScopeSelector
            availablePrograms={getProgramCodes()}
            onChange={setProgramScope}
          />
          <Button
            variant="normal"
            iconName={showAll ? 'user-profile' : 'group'}
            onClick={handleToggleShowAll}
          >
            {showAll ? 'My Fund Requests' : 'Show All'}
          </Button>
        </SpaceBetween>
      </Box>
      <FundingRequestTable
        fundingRequests={filteredByProgram}
        totalUnfilteredCount={fundingRequests.length}
        loading={loading}
        onRefresh={() => loadFundingRequests(showAll, daysFilter)}
        onViewRequest={handleViewRequest}
        daysFilter={showAll ? daysFilter : null}
        onDaysFilterChange={handleDaysFilterChange}
        initialTableView={initialTableView}
        onTableViewChange={handleTableViewChange}
      />
    </SpaceBetween>
  );
};

export default FundingRequestTracker;
